import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import { Button, Card, cn } from '../../components/ui/Kit';
import EmptyState from '../../components/ui/EmptyState';
import Spinner from '../../components/ui/Spinner';
import Modal from '../../components/ui/Modal';
import { useToast } from '../../components/ui/Toast';
import SafeIcon from '../../common/SafeIcon';
import { FUNNEL_ADMIN_ROLES } from '../../lib/funnelUtils';
import * as FiIcons from 'react-icons/fi';

const { FiArrowLeft, FiLayers, FiTrash2 } = FiIcons;

export default function FunnelTemplates() {
  const { user } = useAuth();
  const { addToast } = useToast();
  const navigate = useNavigate();

  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  const isAdmin = FUNNEL_ADMIN_ROLES.includes(user?.role);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api.funnelTemplates.getAll();
      setTemplates(data ?? []);
    } catch (err) {
      addToast({ type: 'error', title: 'Failed to load templates', message: err.message });
    } finally {
      setLoading(false);
    }
  }, [addToast]);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setActionLoading(true);
    try {
      await api.funnelTemplates.delete(deleteTarget.id);
      addToast({ type: 'success', title: 'Template deleted' });
      setDeleteTarget(null);
      await load();
    } catch (err) {
      addToast({ type: 'error', title: 'Delete failed', message: err.message });
    } finally {
      setActionLoading(false);
    }
  };

  const systemTemplates = templates.filter((t) => t.is_system);
  const agencyTemplates = templates.filter((t) => !t.is_system);

  const TemplateGrid = ({ items, showDelete }) => (
    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
      {items.map((t) => (
        <Card key={t.id} className="p-4 relative">
          <div className="w-full h-24 rounded-md mb-3 bg-gradient-to-br from-sidebar to-primary/30 flex items-center justify-center">
            <SafeIcon icon={FiLayers} className="w-8 h-8 text-white/80" />
          </div>
          <p className="font-semibold text-gray-900">{t.name}</p>
          {t.description && (
            <p className="text-xs text-gray-500 mt-1 line-clamp-2">{t.description}</p>
          )}
          <p className="text-xs text-gray-400 mt-2">{t.step_count ?? 0} step(s)</p>
          {showDelete && isAdmin && !t.is_system && (
            <button
              type="button"
              title="Delete template"
              onClick={() => setDeleteTarget(t)}
              className="absolute top-3 right-3 p-1.5 rounded-md text-gray-400 hover:text-red-600 hover:bg-red-50"
            >
              <SafeIcon icon={FiTrash2} className="w-4 h-4" />
            </button>
          )}
        </Card>
      ))}
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={() => navigate('/funnels')}>
          <SafeIcon icon={FiArrowLeft} className="mr-1" />
          Back to Funnels
        </Button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Funnel Templates</h1>
          <p className="text-sm text-gray-500">Reusable starting points for new funnels.</p>
        </div>
      </div>

      {loading ? (
        <div className="flex flex-col items-center justify-center py-16 gap-3">
          <Spinner size="lg" className="text-primary" />
        </div>
      ) : templates.length === 0 ? (
        <EmptyState
          icon={FiLayers}
          title="No templates yet"
          description="System templates will appear here once seeded. Save a funnel as a template from the builder."
        />
      ) : (
        <>
          {systemTemplates.length > 0 && (
            <section>
              <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">System templates</h2>
              <TemplateGrid items={systemTemplates} showDelete={false} />
            </section>
          )}
          {agencyTemplates.length > 0 && (
            <section>
              <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">Agency templates</h2>
              <TemplateGrid items={agencyTemplates} showDelete />
            </section>
          )}
        </>
      )}

      <Modal isOpen={Boolean(deleteTarget)} onClose={() => setDeleteTarget(null)} title="Delete template?">
        <p className="text-sm text-gray-600 mt-2">
          Delete <span className="font-medium">{deleteTarget?.name}</span>? Existing funnels using this template will not be affected.
        </p>
        <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-gray-100">
          <Button variant="outline" size="sm" onClick={() => setDeleteTarget(null)} disabled={actionLoading}>Cancel</Button>
          <Button variant="danger" size="sm" onClick={handleDelete} isLoading={actionLoading}>Delete</Button>
        </div>
      </Modal>
    </div>
  );
}
